<template>
  <div class="category-form">
    <HMModalHeader
      :title="$t('MEDIA_LIBRARY.TITLE.CATEGORY')"
      @close="$emit('close')"
    />
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <form @submit.prevent="submitCategory">
        <HMTextInput
          type="text"
          rules="required"
          :label="$t('MEDIA_LIBRARY.LABEL.CATEGORY_NAME')"
          v-model="categoryModel.name"
          name="Category Name"
        />
        <div class="form-group">
          <label for="tag">
            {{ $t('MEDIA_LIBRARY.LABEL.TAGS') }}
          </label>
          <div class="d-flex">
            <div class="position-relative w-100">
              <input
                type="text"
                class="form-control"
                id="tag"
                v-model="option.name"
              />
              <span
                class="dismiss-edit-option-group"
                @click="dismissEditOption"
                v-show="isUpdateOption"
              >
                <span class="dismiss-edit-option">
                  <font-awesome-icon :icon="['far', 'times']" />
                </span>
              </span>
            </div>
            <HMButton
              color="primary ml-2 text-nowrap"
              :disabled="!option.name.length"
              @click="addOption"
              v-show="!isUpdateOption"
            >
              {{ $t('MEDIA_LIBRARY.BUTTON.ADD_TAG') }}
            </HMButton>
            <HMButton
              color="primary ml-2 text-nowrap"
              :disabled="!option.name.length"
              @click="updateOption"
              v-show="isUpdateOption"
            >
              {{ $t('MEDIA_LIBRARY.BUTTON.UPDATE_TAG') }}
            </HMButton>
          </div>
          <draggable
            :delay="draggableDelay"
            tag="div"
            v-model="draggableOptionList"
            class="mt-2"
          >
            <HMBadge
              class="tag"
              v-for="(option, $index) in draggableOptionList"
              :key="$index"
              @remove="removeOptionOnIndex($index)"
              @edit="editOption(option)"
            >
              {{ option.name }}
            </HMBadge>
          </draggable>
        </div>

        <HMModalFooter
          :submitButton="$t('GENERAL.BUTTON.SAVE')"
          :disabled="invalid || inProgress"
          :loading="inProgress"
          :formError="formError"
          @close="$emit('close')"
        >
          <template v-slot:close>
            <div></div>
          </template>
        </HMModalFooter>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import { get, cloneDeep } from 'lodash';
import draggable from 'vuedraggable';
import { draggableDelay } from '@/web/utils/draggable';
import { mapState } from 'vuex';

function getNewOption() {
  return {
    localId: null,
    id: null,
    name: '',
    position: null,
  };
}

export default {
  components: {
    ValidationObserver,
    draggable,
  },
  props: {
    category: {
      type: Object,
      default() {
        return {
          category_id: null,
          name: null,
          option_list: [],
          position: null,
        };
      },
    },
  },
  data() {
    return {
      draggableDelay,
      categoryModel: {},
      formError: null,
      option: getNewOption(),
      optionListHasChanged: false,
      inProgress: false,
    };
  },
  computed: {
    draggableOptionList: {
      get() {
        return this.categoryModel.option_list;
      },
      set(list) {
        list.forEach((option, index) => (option.position = index));
        this.categoryModel.option_list = list;
      },
    },
    isUpdateOption() {
      return this.option.id || this.option.localId;
    },
    ...mapState('mediaLibrary', ['categories']),
  },
  methods: {
    async submitCategory() {
      this.inProgress = true;
      const dispatchAction = this.category.category_id
        ? 'mediaLibrary/updateCategory'
        : 'mediaLibrary/createCategory';

      const categoryModel = cloneDeep(this.categoryModel);
      categoryModel.option_list.forEach(option => {
        delete option.localId;
      });

      try {
        const dispatchPromise = await this.$store.dispatch(
          dispatchAction,
          categoryModel
        );
        await this.$store.dispatch('mediaLibrary/getCategories');
        this.$emit('close');
        this.$emit('onSubmit', dispatchPromise);
      } catch (error) {
        this.formError = get(error, 'response.data.non_field_errors[0]', null);
        this.inProgress = false;
      }
    },
    addOption() {
      if (!this.option.id) {
        this.option.localId = Date.now();
        this.option.position = this.categoryModel.option_list.length;
      }

      this.categoryModel.option_list.push(this.option);
      this.option = getNewOption();
    },
    updateOption() {
      const list = this.categoryModel.option_list;
      const optionIndex = list.findIndex(option => {
        return (
          option.id === this.option.id || option.localId === this.option.id
        );
      });
      list.splice(optionIndex, 1, { ...this.option });
      this.categoryModel.option_list = list;
      this.option = getNewOption();
    },
    removeOptionOnIndex(optionIndex) {
      const list = this.categoryModel.option_list;
      list.splice(optionIndex, 1);
      list.forEach((option, index) => (option.position = index));
    },
    editOption(option) {
      this.option = { ...option };
    },
    dismissEditOption() {
      this.option = getNewOption();
    },
  },
  watch: {
    'categoryModel.option_list'() {
      const iList = this.category.option_list;
      const mList = this.categoryModel.option_list;
      this.optionListHasChanged =
        iList.length !== mList.length ||
        iList.some((o, index) => o.id !== mList[index].id);
    },
  },
  mounted() {
    if (this.category.position === null)
      this.category.position = this.categories.length;
    this.categoryModel = cloneDeep(this.category);
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.tag {
  cursor: grabbing;
  margin: 0.2rem;
}

.dismiss-edit-option-group {
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  right: 0;
  top: 0;
  bottom: 0;
  width: 42px;
}

.dismiss-edit-option {
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  background-color: $medium-gray;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  &:hover {
    background-color: darken($medium-gray, 15%);
  }
}
</style>
